import { ArrowLeft, Bell, CheckCheck } from "lucide-react";
import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/shared/lib/use-mobile";
import { InfoTooltip } from "@/shared/ui/info-tooltip";
import { Button } from "@/shared/ui/kit/button";
import { Skeleton } from "@/shared/ui/kit/skeleton";
import { UnifiedDrawer } from "@/shared/ui/unified-drawer";

import {
  useNotificationsModel,
  type Notification,
} from "./model/use-notifications-model";
import { NotificationListSkeleton } from "./ui/notification-card-skeleton";
import { NotificationDetail } from "./ui/notification-detail";
import { NotificationEmpty } from "./ui/notification-empty";
import { NotificationList } from "./ui/notification-list";

// ============================================================================
// Types
// ============================================================================

type NotificationsSheetProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  scrollOffset?: number;
  onScrollOffsetChange?: (offset: number) => void;
};

// ============================================================================
// Skeletons
// ============================================================================

function NotificationDetailSkeleton() {
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function NotificationsSheet({
  isOpen,
  onOpenChange,
  scrollOffset = 0,
  onScrollOffsetChange,
}: NotificationsSheetProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const model = useNotificationsModel(20);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Selected notification state (for detail view)
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [isPendingDetail, setIsLoadingDetail] = useState(false);

  // Handle drawer close - save scroll position
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && scrollContainerRef.current && onScrollOffsetChange) {
        onScrollOffsetChange(scrollContainerRef.current.scrollTop);
      }
      onOpenChange(open);
    },
    [onOpenChange, onScrollOffsetChange],
  );

  // Handle notification click
  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      // Save scroll position before navigating to detail
      if (scrollContainerRef.current && onScrollOffsetChange) {
        onScrollOffsetChange(scrollContainerRef.current.scrollTop);
      }

      setSelectedNotification(notification);
      setIsLoadingDetail(true);

      try {
        const detail = await model.getNotificationDetail(notification.id);
        setSelectedNotification(detail);

        // Mark as read silently
        if (detail.unread) {
          model.markAsRead(detail.id);
        }
      } catch {
        if (notification.unread) {
          model.markAsRead(notification.id);
        }
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [model, onScrollOffsetChange],
  );

  const handleBackToList = useCallback(() => {
    setSelectedNotification(null);
  }, []);

  const showDetail = selectedNotification !== null;

  // List content (for desktop - has flex-1)
  const listContent = model.isPending ? (
    <div className="flex-1 overflow-y-auto">
      <NotificationListSkeleton count={5} />
    </div>
  ) : model.isError ? (
    <div className="text-center flex-1 py-16 text-destructive px-6">
      {t("notifications.errors.loadFailed", "Failed to load notifications")}
    </div>
  ) : model.isEmpty ? (
    <NotificationEmpty />
  ) : (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
      <NotificationList
        notifications={model.notifications}
        onNotificationClick={handleNotificationClick}
        scrollContainerRef={scrollContainerRef}
        cursorRef={model.cursorRef}
        isFetchingNextPage={model.isFetchingNextPage}
        savedScrollOffset={scrollOffset}
      />
    </div>
  );

  // List content for mobile (no flex-1 - handled by parent)
  const mobileListContent = model.isPending ? (
    <NotificationListSkeleton count={5} />
  ) : model.isError ? (
    <div className="text-center py-16 text-destructive px-6">
      {t("notifications.errors.loadFailed", "Failed to load notifications")}
    </div>
  ) : model.isEmpty ? (
    <NotificationEmpty />
  ) : (
    <NotificationList
      notifications={model.notifications}
      onNotificationClick={handleNotificationClick}
      scrollContainerRef={scrollContainerRef}
      cursorRef={model.cursorRef}
      isFetchingNextPage={model.isFetchingNextPage}
      savedScrollOffset={scrollOffset}
    />
  );

  // Detail content
  const detailContent = isPendingDetail ? (
    <NotificationDetailSkeleton />
  ) : selectedNotification ? (
    <NotificationDetail notification={selectedNotification} />
  ) : null;

  // Header for detail view
  const detailHeader = (
    <div className="flex items-center gap-3 w-full">
      <button
        onClick={handleBackToList}
        className="shrink-0 size-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
        aria-label={t("common.back", "Назад")}
      >
        <ArrowLeft className="size-4" />
      </button>
      <div className="flex-1 min-w-0">
        <UnifiedDrawer.Title className="text-base font-semibold truncate">
          {selectedNotification?.title}
        </UnifiedDrawer.Title>
        <p className="text-xs text-muted-foreground">
          {selectedNotification &&
            new Date(selectedNotification.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );

  // Header for list view
  const listHeader = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
          <Bell className="h-4 w-4 text-primary" />
        </div>
        <div className="flex flex-col">
          <UnifiedDrawer.Title className="text-base font-semibold inline-flex items-center gap-1.5">
            {t("notifications.title", "Уведомления")}
            <InfoTooltip tKey="notifications.info.types" />
          </UnifiedDrawer.Title>
          <p className="text-xs text-muted-foreground">
            {t("notifications.subtitle", "Ваши последние уведомления")}
          </p>
        </div>
      </div>
      {model.unreadCount > 0 && (
        <div className="flex items-center gap-1.5 bg-primary text-primary-foreground px-2 py-1 rounded-full">
          <span className="text-xs font-medium">{model.unreadCount}</span>
          {!model.isEmpty && !model.isPending && (
            <Button
              variant="ghost"
              size="sm"
              onClick={model.markAllAsRead}
              disabled={model.isMarkingAllRead}
              className="size-5 p-0 hover:bg-primary-foreground/20"
            >
              <CheckCheck className="size-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const direction = isMobile ? "bottom" : "right";

  return (
    <UnifiedDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      direction={direction}
    >
      <UnifiedDrawer.Content
        className={isMobile ? undefined : "w-full max-w-[450px] rounded-l-2xl"}
      >
        <UnifiedDrawer.Header className="shrink-0">
          {showDetail ? detailHeader : listHeader}
        </UnifiedDrawer.Header>
        {showDetail ? (
          <UnifiedDrawer.Body className="overflow-y-auto p-4">
            {detailContent}
          </UnifiedDrawer.Body>
        ) : (
          <UnifiedDrawer.Body
            ref={scrollContainerRef}
            className="overflow-y-auto p-0 no-scrollbar"
          >
            {isMobile ? mobileListContent : listContent}
          </UnifiedDrawer.Body>
        )}
      </UnifiedDrawer.Content>
    </UnifiedDrawer>
  );
}
